import { fg } from '@atlaskit/platform-feature-flags/fg';

import {
	emitFrontendLogs as emitFrontendLogsQuery,
	type FrontendLogEntry,
} from '../../web-client/queries/emitFrontendLogs';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

type EmitFrontendLogsPayload = {
	logs: FrontendLogEntry[];
};

type EmitFrontendLogsContext = MakeBridgeContext<
	'extension' | 'extensionDetails' | 'contextIds',
	'relayEnvironment'
>;

/**
 * Emits Forge frontend logs from a Custom UI iframe.
 *
 * Enriches app-provided log entries with trusted host context and forwards them to
 * AGG. Gated on `platform_forge_frontend_logs`; fire-and-forget and silent-fail so
 * logging can never surface an error to the app.
 */
export const emitFrontendLogs: TBridgeMethod<
	EmitFrontendLogsContext,
	EmitFrontendLogsPayload,
	void
> = async (
	{ extension, extensionDetails, contextIds, services: { relayEnvironment } },
	payload,
) => {
	if (!fg('platform_forge_frontend_logs')) {
		return;
	}

	const contextAri = contextIds[0];
	if (!contextAri || payload.logs.length === 0) {
		return;
	}

	try {
		await emitFrontendLogsQuery({
			relayEnvironment,
			contextAri,
			appId: extensionDetails.appId,
			installationId: extension.installationId,
			extensionId: extension.id,
			logs: payload.logs,
		});
	} catch {
		// Silent fail: emitting logs must never surface an error to the app.
	}
};
